import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { calculateCart, processPayment } from '../utils/apiTransform';
import html2pdf from 'html2pdf.js';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();
  const { selectedLocation } = useLocation();
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [cartCalculation, setCartCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Ref for PDF download
  const orderConfirmationRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pickupDate: '',
    pickupTime: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipcode: '',
    specialInstructions: '',
  });

  // Calculate cart totals whenever cart items change
  useEffect(() => {
    const fetchCartCalculation = async () => {
      if (cartItems.length === 0) {
        setCartCalculation(null);
        setCalculationError(null);
        return;
      }

      if (!selectedLocation) {
        return;
      }

      try {
        setIsCalculating(true);
        setCalculationError(null);

        const response = await calculateCart(cartItems, selectedLocation);

        if (response.status === 'error') {
          // Clear cart and show error message
          setCalculationError('Server encountered an error. Cart has been reset.');
          clearCart();
          setCartCalculation(null);
        } else {
          setCartCalculation(response);
        }
      } catch (error) {
        console.error('Error calculating cart:', error);
        setCalculationError('Failed to calculate cart totals. Please try again.');
      } finally {
        setIsCalculating(false);
      }
    };

    fetchCartCalculation();
  }, [cartItems, selectedLocation, clearCart]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Phone number formatting - only allow digits, max 10
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedDigits }));
      return;
    }

    // Expiry date formatting - YYYY-MM format
    if (name === 'expiryDate') {
      const digitsOnly = value.replace(/\D/g, '');
      let formattedValue = digitsOnly;

      // Add hyphen after 4 digits (YYYY-MM)
      if (digitsOnly.length >= 5) {
        formattedValue = digitsOnly.slice(0, 4) + '-' + digitsOnly.slice(4, 6);
      }

      setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      return;
    }

    // If pickup date changes, validate pickup time
    if (name === 'pickupDate') {
      setFormData((prev) => {
        const newFormData = { ...prev, [name]: value };

        // If switching to December 24 and current time is after 5 PM, clear it
        if (value === 'December 24' && prev.pickupTime) {
          const restrictedTimes = ['6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM'];
          if (restrictedTimes.includes(prev.pickupTime)) {
            newFormData.pickupTime = '';
          }
        }

        return newFormData;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const response = await processPayment({
        location: selectedLocation,
        cartItems: cartItems,
        formData: formData
      });

      if (response.status === 'success') {
        setOrderResponse(response.data);
        // Save order details before clearing cart
        setOrderDetails({
          items: [...cartItems],
          pickupLocation: selectedLocation,
          pickupDate: formData.pickupDate,
          pickupTime: formData.pickupTime,
          firstName: formData.firstName,
          lastName: formData.lastName,
          calculation: cartCalculation
        });
        setOrderPlaced(true);
        clearCart();
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmissionError(response.message || 'Payment processing failed. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      setSubmissionError('An error occurred while processing your order. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!orderConfirmationRef.current) return;

    setIsDownloadingPDF(true);

    try {
      const element = orderConfirmationRef.current;

      const opt = {
        margin: 0.5,
        filename: `Belle-Journee-Order-${orderResponse?.order_number || 'Confirmation'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: {
          unit: 'in',
          format: 'letter',
          orientation: 'portrait'
        }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (cartItems.length === 0 && !orderPlaced) {
    navigate('/cart');
    return null;
  }

  if (orderPlaced) {
    return (
      <div className="checkout-page">
        <div className="order-success">
          <h2>Order Placed Successfully!</h2>

          {orderResponse && (
            <div className="order-confirmation-card" ref={orderConfirmationRef}>
              <div className="confirmation-section">
                <h3>Order Information</h3>
                <p><strong>Order Number:</strong> {orderResponse.order_number}</p>
                {orderDetails && (
                  <p><strong>Customer:</strong> {orderDetails.firstName} {orderDetails.lastName}</p>
                )}
              </div>

              {orderDetails && (
                <>
                  <div className="confirmation-section">
                    <h3>Pickup Details</h3>
                    <p><strong>Location:</strong> {orderDetails.pickupLocation}</p>
                    <p><strong>Date:</strong> {orderDetails.pickupDate}</p>
                    <p><strong>Time:</strong> {orderDetails.pickupTime}</p>
                  </div>

                  <div className="confirmation-section">
                    <h3>Order Details</h3>
                    <div className="order-items-list">
                      {orderDetails.items.map((item) => (
                        <div key={item.id} className="order-item-row">
                          <div className="order-item-info">
                            <span className="order-item-name">{item.name}</span>
                            <span className="order-item-qty">x{item.quantity}</span>
                          </div>
                          <span className="order-item-price">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="confirmation-section totals-section">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>${orderResponse.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Tax:</span>
                      <span>${orderResponse.tax.toFixed(2)}</span>
                    </div>
                    <div className="total-row final-total">
                      <span>Total:</span>
                      <span>${orderResponse.total.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <p className="confirmation-message">Thank you for your order. You will receive a confirmation email shortly.</p>
          <div className="confirmation-buttons">
            <button
              className="download-pdf-btn"
              onClick={handleDownloadPDF}
              disabled={isDownloadingPDF}
            >
              {isDownloadingPDF ? 'Generating PDF...' : 'Download PDF'}
            </button>
            <button
              className="return-to-menu-btn"
              onClick={() => navigate('/')}
            >
              Return to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h2 className="checkout-title">Checkout</h2>

        {calculationError && (
          <div className="error-message">
            <p>{calculationError}</p>
          </div>
        )}

        <div className="checkout-content">
          <form className="checkout-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="1234567890"
                    maxLength="10"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Pickup Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Pickup Location</label>
                  <div className="location-display-box">
                    {selectedLocation}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Pickup Date *</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="December 23"
                        checked={formData.pickupDate === 'December 23'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">December 23</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="December 24"
                        checked={formData.pickupDate === 'December 24'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">December 24</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="December 25"
                        checked={formData.pickupDate === 'December 25'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">December 25</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="pickupTime">Pickup Time *</label>
                  <select
                    id="pickupTime"
                    name="pickupTime"
                    value={formData.pickupTime}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a time</option>
                    <option value="7:00 AM">7:00 AM</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                    {formData.pickupDate !== 'December 24' && (
                      <>
                        <option value="6:00 PM">6:00 PM</option>
                        <option value="7:00 PM">7:00 PM</option>
                        <option value="8:00 PM">8:00 PM</option>
                        <option value="9:00 PM">9:00 PM</option>
                        <option value="10:00 PM">10:00 PM</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Payment Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="cardNumber">Card Number *</label>
                  <input
                    type="text"
                    id="cardNumber"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleInputChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cardName">Cardholder Name *</label>
                  <input
                    type="text"
                    id="cardName"
                    name="cardName"
                    value={formData.cardName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="expiryDate">Expiry Date (YYYY-MM) *</label>
                  <input
                    type="text"
                    id="expiryDate"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    placeholder="2025-12"
                    maxLength="7"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cvv">CVV *</label>
                  <input
                    type="text"
                    id="cvv"
                    name="cvv"
                    value={formData.cvv}
                    onChange={handleInputChange}
                    placeholder="123"
                    maxLength="4"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Billing Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="billingAddress">Billing Address *</label>
                  <input
                    type="text"
                    id="billingAddress"
                    name="billingAddress"
                    value={formData.billingAddress}
                    onChange={handleInputChange}
                    placeholder="123 Main Street"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billingCity">City *</label>
                  <input
                    type="text"
                    id="billingCity"
                    name="billingCity"
                    value={formData.billingCity}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="billingState">State *</label>
                  <select
                    id="billingState"
                    name="billingState"
                    value={formData.billingState}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select state</option>
                    <option value="AL">AL</option>
                    <option value="AK">AK</option>
                    <option value="AZ">AZ</option>
                    <option value="AR">AR</option>
                    <option value="CA">CA</option>
                    <option value="CO">CO</option>
                    <option value="CT">CT</option>
                    <option value="DE">DE</option>
                    <option value="FL">FL</option>
                    <option value="GA">GA</option>
                    <option value="HI">HI</option>
                    <option value="ID">ID</option>
                    <option value="IL">IL</option>
                    <option value="IN">IN</option>
                    <option value="IA">IA</option>
                    <option value="KS">KS</option>
                    <option value="KY">KY</option>
                    <option value="LA">LA</option>
                    <option value="ME">ME</option>
                    <option value="MD">MD</option>
                    <option value="MA">MA</option>
                    <option value="MI">MI</option>
                    <option value="MN">MN</option>
                    <option value="MS">MS</option>
                    <option value="MO">MO</option>
                    <option value="MT">MT</option>
                    <option value="NE">NE</option>
                    <option value="NV">NV</option>
                    <option value="NH">NH</option>
                    <option value="NJ">NJ</option>
                    <option value="NM">NM</option>
                    <option value="NY">NY</option>
                    <option value="NC">NC</option>
                    <option value="ND">ND</option>
                    <option value="OH">OH</option>
                    <option value="OK">OK</option>
                    <option value="OR">OR</option>
                    <option value="PA">PA</option>
                    <option value="RI">RI</option>
                    <option value="SC">SC</option>
                    <option value="SD">SD</option>
                    <option value="TN">TN</option>
                    <option value="TX">TX</option>
                    <option value="UT">UT</option>
                    <option value="VT">VT</option>
                    <option value="VA">VA</option>
                    <option value="WA">WA</option>
                    <option value="WV">WV</option>
                    <option value="WI">WI</option>
                    <option value="WY">WY</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billingZipcode">Zipcode *</label>
                  <input
                    type="text"
                    id="billingZipcode"
                    name="billingZipcode"
                    value={formData.billingZipcode}
                    onChange={handleInputChange}
                    placeholder="12345"
                    maxLength="10"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section" style={{ display: 'none' }}>
              <h3>Special Instructions</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="specialInstructions">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="specialInstructions"
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Any special requests or dietary restrictions?"
                  />
                </div>
              </div>
            </div>

            {submissionError && (
              <div className="error-message" style={{ marginTop: '1rem' }}>
                <p>{submissionError}</p>
              </div>
            )}

            <button
              type="submit"
              className="place-order-btn"
              disabled={!cartCalculation || cartCalculation.status !== 'success' || isCalculating || isSubmitting}
            >
              {isSubmitting ? 'Processing Payment...' : isCalculating ? 'Calculating...' : `Place Order - $${cartCalculation?.cart_summary?.total.toFixed(2) || '0.00'}`}
            </button>
          </form>

          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-items">
              {cartItems.map((item) => {
                return (
                  <div key={item.id} className="summary-item">
                    <div className="summary-item-info">
                      <span className="summary-item-name">{item.name}</span>
                      <span className="summary-item-qty">x{item.quantity}</span>
                    </div>
                    <span className="summary-item-price">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="summary-totals">
              {isCalculating ? (
                <div className="summary-loading">
                  <p>Calculating totals...</p>
                </div>
              ) : cartCalculation && cartCalculation.status === 'success' ? (
                <>
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>${cartCalculation.cart_summary.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax</span>
                    <span>${cartCalculation.cart_summary.tax.toFixed(2)}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>${cartCalculation.cart_summary.total.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="summary-row">
                  <span>Calculating...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
